import React, {createContext, useState} from 'react';
import {FORM_JOURNEY} from "../constants/types";

export interface UserProfile {
    vehiclesInfo: VehicleInfoDto[];
    optedVehicle: VehicleInfoDto;
    conditions: any;
    optedUtility: UtilityAccountInformation;
    optedLocation: any;
    utilityAcknowledgement: any;
    journeyContext: any;
    currentScreen: any;
    others: any;
    personalInfo: PersonalInfo;
    termsAccepted: boolean;
    alreadyEnrolledVehicles: EnrolledVehiclesInfo[];
}

export interface EnrollmentRequest {
    optedVehicle: VehicleInfoDto;
    personalInfo: PersonalInfo;
    optedUtility: UtilityAccountInformation;
    conditions: any;
    termsAccepted: boolean;
}

export class EnrollmentRequestDto implements EnrollmentRequest {
    optedVehicle: VehicleInfoDto;
    personalInfo: PersonalInfo;
    optedUtility: UtilityAccountInformation;
    conditions: any;
    termsAccepted: boolean;

    constructor(profileContext: UserProfile) {
        this.optedVehicle = profileContext.optedVehicle;
        this.personalInfo = {
            ...profileContext.personalInfo,
            homeAddress: profileContext.personalInfo.homeAddress?.trim() || '',
            city: profileContext.personalInfo.city?.trim() || '',
            aptUnit: profileContext.personalInfo.aptUnit?.trim() || '',
        };
        this.optedUtility = {
            ...profileContext.optedUtility,
            accountHolderFirstName: profileContext.optedUtility.accountHolderFirstName?.trim() || '',
            accountHolderLastName: profileContext.optedUtility.accountHolderLastName?.trim() || '',
        };        
        this.conditions = profileContext.conditions;
        this.termsAccepted = profileContext.termsAccepted;
    }
}

export interface UtilityAccountInformation {
    accountHolderFirstName: string;
    accountHolderLastName: string;
    accountNumber: string;
    utilityProvider: string;
}

export interface PersonalInfo {
    userGuid: string;
    firstName: string;
    lastName: string;
    email: string;
    mobilePhone: string;
    homeAddress: string;
    aptUnit: string;
    city: string;
    state: string;
    zipCode: string;
}

export interface EnrolledVehiclesInfo {
    vin: string;
    applicationStatus: string;
    createdAt: string;
    details: {
    enrollmentId: string;
        address: string;
        aptUnit: string;
        city: string;
        state: string;
        zipcode: string;
        mobileNumber: string;
    }
}

export interface UserProfileResponse {
    profileInfo: UserProfile;
    vehicleInfo: VehicleInfo[];
    enrolledVehicles: EnrolledVehiclesInfo[];
}

export const userProfileInitialValues: UserProfile = {
    vehiclesInfo: [],
    optedVehicle: {
        vin: '',
        vehicleType: '',
        modelName: '',
        modelCode: '',
        modelYear: '',
        color: '',
        make: '',
        tcuEnabled: false,
        ngSdnManaged: false,
        vehicleAuthorizationIndicator: false
    },
    conditions: {},
    optedUtility: {
        accountHolderFirstName: '',
        accountHolderLastName: '',
        accountNumber: '',
        utilityProvider: ''
    },
    optedLocation: '',
    utilityAcknowledgement: '',
    journeyContext: '',
    currentScreen: '',
    others: '',
    termsAccepted: false,
    personalInfo: {
        userGuid: '',
        firstName: '',
        lastName: '',
        email: '',
        mobilePhone: '',
        homeAddress: '',
        aptUnit: '',
        city: '',
        state: '',
        zipCode: ''
    },
    alreadyEnrolledVehicles: [],
};

interface ProfileContextType {
    profileContext: UserProfile;
    updateProfileContext: (value: any) => void;
    clearProfileContext: () => void;
    selectedVehicle: string | null;
    setSelectedVehicle: (vehicle: string | null) => void;
    formState: FORM_JOURNEY,
    setFormState: (value: any) => void,
    selectedProvider: string | null;
    setSelectedProvider: (provider: string) => void;
}

export interface VehicleInfo {
    vin: string;
    vehicleType: string;
    modelName: string;
    modelCode: string;
    modelYear: string;
    color: string;
    make: string;
    tcuEnabled: number;
    ngSdnManaged: number;
    vehicleAuthorizationIndicator: number;
    applicationStatus?: string;
    applicationStatusDescription?: string;
}

export class VehicleInfoDto {
    vin?: string;
    vehicleType?: string;
    modelName?: string;
    modelCode?: string;
    modelYear?: string;
    color?: string;
    make?: string;
    tcuEnabled?: boolean;
    ngSdnManaged?: boolean;
    vehicleAuthorizationIndicator?: boolean;
    applicationStatus?: string;
    applicationStatusDescription?: string;

    constructor(vehicleInfo: VehicleInfo) {
        this.vin = vehicleInfo.vin;
        this.vehicleType = vehicleInfo.vehicleType;
        this.modelName = vehicleInfo.modelName;
        this.modelCode = vehicleInfo.modelCode;
        this.modelYear = vehicleInfo.modelYear;
        this.color = vehicleInfo.color;
        this.make = vehicleInfo.make;
        this.tcuEnabled = vehicleInfo.tcuEnabled === 1;
        this.ngSdnManaged = vehicleInfo.ngSdnManaged === 1;
        this.vehicleAuthorizationIndicator = vehicleInfo.vehicleAuthorizationIndicator === 1;
        this.applicationStatus = vehicleInfo.applicationStatus;
        this.applicationStatusDescription = vehicleInfo.applicationStatusDescription;
    }
}


const ProfileContext = createContext<ProfileContextType>({
        profileContext: userProfileInitialValues,
        updateProfileContext: () => {},
        clearProfileContext: ()=>{},
        selectedVehicle: null,
        setSelectedVehicle: () => {
        },
        formState: FORM_JOURNEY.ACKNOWLEDGEMENT,
        setFormState: () => {
        },
        selectedProvider: null,
        setSelectedProvider: () => {
        },
    },
);

const ProfileContextProvider = ({children}: any) => {
    const [profileContext, setProfileContext] = useState(userProfileInitialValues);
    const [selectedVehicle, setSelectedVehicle] = useState<string | null>(null);
    const [selectedProvider, setSelectedProvider] = useState<string>("");
    const [formState, setFormState] = useState<FORM_JOURNEY>(FORM_JOURNEY.ACKNOWLEDGEMENT);

    const updateProfileContext = React.useCallback((changedContext: any) => {
        setProfileContext(currContext => ({...currContext, ...changedContext}));
    }, []);

    const clearProfileContext = React.useCallback(() => {
        setProfileContext(userProfileInitialValues);
    }, []);

    const contextValue = React.useMemo(() => ({
        profileContext,
        updateProfileContext,
        clearProfileContext,
        selectedVehicle,
        setSelectedVehicle,
        formState,
        setFormState,
        selectedProvider,
        setSelectedProvider
    }), [
        profileContext,
        updateProfileContext,
        clearProfileContext,
        selectedVehicle,
        setSelectedVehicle,
        formState,
        setFormState,
        selectedProvider,
        setSelectedProvider
    ]);

    return (
        <ProfileContext.Provider value={contextValue}>
            {children}
        </ProfileContext.Provider>
    );
};


export { ProfileContext, ProfileContextProvider };
